import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../lib/api';
import { local } from '../lib/storage';
import BusinessSection from '../components/InvoiceForm/BusinessSection';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Save, Upload } from 'lucide-react';
import { defaultBusiness } from '../hooks/useInvoice';
import { Link } from 'react-router-dom';
import { validateEmail, validatePhone } from '../lib/validate';
import { useLanguage } from '../contexts/LanguageContext';

function splitDisplayName(user) {
  const parts = (user?.name || '').trim().split(/\s+/).filter(Boolean);
  return {
    firstName: user?.firstName || parts[0] || '',
    lastName: user?.lastName || parts.slice(1).join(' ') || '',
  };
}

export default function Settings() {
  const { user, refresh, refreshBusiness } = useAuth();
  const { t } = useLanguage();
  const [business, setBusiness] = useState(() => local.getBusiness() || defaultBusiness);
  const [existingId, setExistingId] = useState(null);
  const [account, setAccount] = useState(() => splitDisplayName(user));

  useEffect(() => {
    if (user) {
      setAccount(splitDisplayName(user));
      (async () => {
        try {
          const { data } = await apiClient.get('/businesses');
          if (data?.[0]) {
            setBusiness({ ...data[0], bankDetails: data[0].bankDetails || {} });
            setExistingId(data[0].id);
          }
        } catch {}
      })();
    }
  }, [user]);

  const save = async () => {
    const firstName = account.firstName.trim();
    const lastName = account.lastName.trim();
    const businessName = (business.name || '').trim();
    const businessPhone = (business.phone || '').trim();
    const businessEmail = (business.email || '').trim();

    if (user) {
      if (!firstName) { toast.error(`${t('firstName')} ${t('required')}`); return; }
      if (!lastName) { toast.error(`${t('lastName')} ${t('required')}`); return; }
      if (!user.email?.trim()) { toast.error(`${t('accountEmail')} ${t('required')}`); return; }
    }
    if (!businessName) { toast.error(`${t('businessName')} ${t('required')}`); return; }
    if (!businessPhone) { toast.error(`${t('phone')} ${t('required')}`); return; }
    if (validatePhone(businessPhone)) { toast.error(validatePhone(businessPhone)); return; }
    if (!businessEmail) { toast.error(`${t('businessEmail')} ${t('required')}`); return; }
    if (validateEmail(businessEmail)) { toast.error(validateEmail(businessEmail)); return; }

    const normalizedBusiness = { ...business, name: businessName, phone: businessPhone, email: businessEmail };
    local.setBusiness(normalizedBusiness);
    if (user) {
      try {
        await apiClient.put('/profile', { firstName, lastName });
        if (existingId) await apiClient.put(`/businesses/${existingId}`, normalizedBusiness);
        else { const { data } = await apiClient.post('/businesses', normalizedBusiness); setExistingId(data.id); }
        setBusiness(normalizedBusiness);
        await refresh();
        await refreshBusiness();
        toast.success(t('profileSaved'));
      } catch (e) { toast.error(t('profileSaveFailed')); }
    } else {
      setBusiness(normalizedBusiness);
      toast.success(t('savedOnBrowser'));
    }
  };

  const handleSealSignature = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error(t('sealSignatureSizeError'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setBusiness((b) => ({ ...b, sealSignature: reader.result }));
    reader.readAsDataURL(file);
  };

  return (
    <div className="max-w-[1000px] mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">{t('settings')}</h1>
        <p className="text-sm text-slate-500 mt-1">{t('settingsIntro')}</p>
      </div>

      {user && (
        <section className="bg-white rounded-xl border border-slate-200 p-5 sm:p-6 shadow-sm" data-testid="account-profile-section">
          <div className="mb-4">
            <h3 className="font-heading text-lg font-bold text-slate-900">{t('accountProfile')}</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="first-name">{t('firstName')}</Label>
              <Input
                id="first-name"
                value={account.firstName}
                onChange={(e) => setAccount((a) => ({ ...a, firstName: e.target.value }))}
                placeholder={t('firstName')}
                required
                data-testid="profile-first-name-input"
              />
            </div>
            <div>
              <Label htmlFor="last-name">{t('lastName')}</Label>
              <Input
                id="last-name"
                value={account.lastName}
                onChange={(e) => setAccount((a) => ({ ...a, lastName: e.target.value }))}
                placeholder={t('lastName')}
                required
                data-testid="profile-last-name-input"
              />
            </div>
            <div>
              <Label htmlFor="account-email">{t('email')}</Label>
              <Input id="account-email" type="email" value={user.email || ''} readOnly required data-testid="profile-email-input" />
            </div>
          </div>
        </section>
      )}

      <BusinessSection business={business} setBusiness={setBusiness} />

      <section className="bg-white rounded-xl border border-slate-200 p-5 sm:p-6 shadow-sm" data-testid="seal-signature-section">
        <div className="mb-4">
          <h3 className="font-heading text-lg font-bold text-slate-900">{t('sealSignature')}</h3>
          <p className="text-sm text-slate-500 mt-1">{t('sealSignatureIntro')}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4 items-center">
          <label className="flex h-32 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-200 bg-slate-50 text-center transition-colors hover:border-orange-300 hover:bg-orange-50" data-testid="seal-signature-upload-label">
            {business.sealSignature ? (
              <img src={business.sealSignature} alt={t('sealSignatureAlt')} className="max-h-24 max-w-[220px] object-contain" />
            ) : (
              <div className="px-3 text-xs font-semibold text-slate-500">
                <Upload className="mx-auto mb-2 h-5 w-5 text-orange-500" />
                {t('uploadSealSignature')}
              </div>
            )}
            <input type="file" accept="image/*" className="hidden" onChange={handleSealSignature} data-testid="seal-signature-upload-input" />
          </label>
          <div className="text-sm text-slate-500 leading-relaxed">
            {t('sealSignatureHelp')}
            {business.sealSignature && (
              <div className="mt-3">
                <Button type="button" variant="outline" size="sm" onClick={() => setBusiness((b) => ({ ...b, sealSignature: '' }))}>
                  {t('remove')}
                </Button>
              </div>
            )}
          </div>
        </div>
      </section>

      <div className="flex gap-3 justify-end">
        <Button onClick={save} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="save-business-btn">
          <Save className="w-4 h-4 mr-2" /> {t('saveProfile')}
        </Button>
      </div>

      <div className="text-center text-xs text-slate-400">
        {t('legalConsent')}{' '}
        <Link to="/legal" className="font-bold text-orange-600 hover:text-orange-700 underline underline-offset-2">
          {t('legalTerms')}
        </Link>
        {' · '}
        <Link to="/changelog" className="font-bold text-orange-600 hover:text-orange-700 underline underline-offset-2">
          {t('changelog')}
        </Link>
      </div>

      {!user && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-5 flex items-center justify-between gap-4">
          <div>
            <div className="font-heading font-bold text-slate-900">{t('wantCloudSync')}</div>
            <p className="text-sm text-slate-600">{t('cloudSyncCopy')}</p>
          </div>
          <Link to="/plan"><Button className="bg-orange-500 hover:bg-orange-600 text-white">{t('viewPlan')}</Button></Link>
        </div>
      )}
    </div>
  );
}
